import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Quote } from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import { IMG } from '../../lib/images';
import Magnetic from '../innovher/Magnetic';
import FloatingOrbs from '../innovher/FloatingOrbs';
import SEO from '../SEO';

export default function FounderPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const beliefs = [
    { t: 'Disrupt the status quo', d: 'We were built to shatter the glass ceiling of opportunity, not to politely sidestep it.' },
    { t: 'Empower every founder', d: 'Regardless of gender, geography or background, ambition is the only currency that matters.' },
    { t: 'Rewrite the rules', d: 'The next decade of innovation will be inclusive, founder-first, and built from tier-2 cities.' },
    { t: 'Equality + innovation', d: 'They aren\u2019t a trade-off. They are the same compounding force.' },
  ];

  return (
    <>
      <SEO 
        title="Founder's Note | Dr. Shweta Choudhary"
        description="A note from Dr. Shweta Choudhary, Founding Director of InnovHer, on why the ecosystem exists and the future it is committed to building."
        keywords="Dr Shweta Choudhary, InnovHer founder, venture builder Jaipur, founder note InnovHer"
      />
      <PageHeader
        eyebrow="Founder’s Note"
        title={<>Igniting a revolution for <span className="text-[#ff8ec5]">inclusive</span> innovation.</>}
        subtitle="A note from the founder on why Innovher exists, and the future it is committed to building."
        image={IMG.innovherFounder}
        breadcrumbs={[{ label: 'Founder’s Note' }]}
      />

      <section className="relative bg-[#08080a] py-24 lg:py-28 overflow-hidden">
        <FloatingOrbs />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal-up">
            <div className="sticky top-32">
              <div className="rounded-3xl overflow-hidden aspect-[4/5] border border-white/10">
                <img src={IMG.shwetaProfile} alt="Founder" className="w-full h-full object-cover" />
              </div>
              <div className="mt-5">
                <div className="text-xl font-semibold text-white">Shweta Choudhary</div>
                <div className="text-white/55 text-sm uppercase tracking-wide">FOUNDING DIRECTOR & INNOVHER</div>
              </div>
            </div>
          </div>

          <article className="lg:col-span-8 reveal-up">
            <Quote className="w-10 h-10 text-[#d42b92]" />
            <h2 className="mt-5 font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
              Innovher is not a platform. It is a <span className="text-[#d42b92]">movement</span>.
            </h2>
            <div className="mt-8 space-y-5 text-white/70 leading-relaxed text-lg">
              <p>We exist to disrupt the status quo and shatter the glass ceiling of opportunity. Our mission is audacious, to empower every startup, regardless of gender or background, and ignite a wildfire of innovation that will change the world.</p>
              <p>We won&rsquo;t settle for the ordinary. Innovher is the launchpad for pioneers, the incubator of brilliance, and the stage for those who dare to dream beyond the stars.</p>
              <p>Every founder who walks through our doors is a potential game-changer. Together, we will sculpt a landscape where every entrepreneur finds their tribe, every investor finds their gem, and together we carve the future.</p>
              <p className="text-white/90 font-medium">We are the architects of a new tomorrow, where equality and innovation go hand in hand.</p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              {beliefs.map((b, i) => (
                <div key={b.t} className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#d42b92] transition group" style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="text-3xl font-semibold text-[#d42b92]">0{i + 1}</div>
                  <div className="mt-3 font-medium text-white">{b.t}</div>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">{b.d}</p>
                </div>
              ))}
            </div>

            <Magnetic>
              <Link to="/apply" data-cursor="hover" className="mt-12 group inline-flex items-center gap-2 pl-6 pr-2 py-2 rounded-full bg-[#d42b92] text-white font-medium hover:bg-white hover:text-black transition">
                Join the movement
                <span className="w-8 h-8 rounded-full bg-white text-[#d42b92] group-hover:bg-[#d42b92] group-hover:text-white grid place-items-center group-hover:rotate-45 transition">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </Magnetic>
          </article>
        </div>
      </section>

      <section className="relative bg-[#0a0a0c] py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-3 gap-3">
          {[IMG.pitch1, IMG.collab2, IMG.pitch4].map((src, i) => (
            <div key={i} className="reveal-up rounded-3xl overflow-hidden aspect-[4/3] border border-white/10">
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
